
import { ApiError } from '@eneco/dc/client/ApiError';
import { chamberOfCommerceSearch } from '@eneco/dc/services';
import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel,
  RequestModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchRequestModel,
} from '@monorepo-types/dc';
import type { EventObject } from 'xstate';
import { assign, raise, setup, fromPromise } from 'xstate';

interface MachineConfig {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
}

interface Values {
  companyName: string;
}

export interface ChamberOfCommerceSearchMachineContext {
  results?: Exclude<Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel['results'], undefined>;

  errorMessage?: string;

  lastCallData: {
    cachedResponse?: Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel;
    eventValues: Values;
  };
}

export interface ChamberOfCommerceSearchMachineEvent extends EventObject {
  type: 'GET_COMPANY_OPTIONS' | 'IDLE' | 'NOT_FOUND';
  config: MachineConfig;
  values: Values;
}

const getChamberOfCommerceSearchService = async (
  config: MachineConfig,
  values: Values,
): Promise<{
  data: Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel;
  values: Values;
}> => {
  const requestBody: RequestModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchRequestModel = {
    data: {
      name: values.companyName || '',
      page: 1,
      amount: 10,
    },
  };
  try {
    const response = await chamberOfCommerceSearch({
      businessUnit: config.businessUnit,
      label: config.label,
      requestBody,
    });

    return { data: response.data as Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel, values };
  } catch (error) {
    throw new Error((error as ApiError)?.message);
  }
};

export const chamberOfCommerceSearchMachine = setup({
  types: {
    context: {} as ChamberOfCommerceSearchMachineContext,
    events: {} as ChamberOfCommerceSearchMachineEvent,
    output: {} as RequestModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchRequestModel,
  },
  actors: {
    fetchChamberOfCommerceSearch: fromPromise<
      { data: Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel; values: Values },
      { config: MachineConfig; values: Values; lastCallData?: ChamberOfCommerceSearchMachineContext['lastCallData'] }
    >(async ({ input }) => {
      const { config, values } = input;

      return await getChamberOfCommerceSearchService(config, values);
    }),
  },
  actions: {
    clearErrorMessage: assign({ errorMessage: undefined }),
    clearContext: assign({
      results: undefined,
      lastCallData: undefined,
    }),
  },
  guards: {
    shouldReturnCatched: ({ context, event }) => {
      const { values } = event;
      const { lastCallData } = context;
      const returnCached = values.companyName === '' || values.companyName === lastCallData?.eventValues?.companyName;

      return returnCached;
    },
  },
}).createMachine({
  id: 'chamberOfCommerceSearch',
  initial: 'IDLE',
  context: {
    lastCallData: {
      eventValues: {
        companyName: '',
      },
    },
  },
  on: {
    GET_COMPANY_OPTIONS: [
      {
        guard: 'shouldReturnCatched',
        target: '.CATCHED',
      },
      {
        target: '.FETCHING',
      },
    ],
  },
  states: {
    IDLE: {},
    FETCHING: {
      invoke: {
        src: 'fetchChamberOfCommerceSearch',
        input: ({ context, event }) => ({
          config: event.config,
          values: event.values,
          lastCallData: context.lastCallData,
        }),
        onDone: {
          target: 'SUCCESS',
          actions: assign(({ event }) => {
            const data = event.output?.data || {};

            const companyName = event.output?.values?.companyName;

            return {
              ...data,
              lastCallData: {
                eventValues: {
                  companyName,
                },
                cachedResponse: event.output?.data,
              },
            };
          }),
        },
        onError: {
          target: 'ERROR',
          actions: [
            ({ event }) =>
              raise({
                type: event.error instanceof Error && event.error.message === 'Not Found' ? 'NOT_FOUND' : '',
              }),
            assign({
              errorMessage: ({ event }) => (event.error instanceof Error ? event.error.message : 'Unknown error'),
            }),
          ],
        },
      },
    },
    CATCHED: {
      entry: ({ context }) => ({
        ...context.lastCallData,
      }),
    },
    SUCCESS: {
      entry: ['clearErrorMessage'],
    },
    ERROR: {
      entry: ['clearContext'],
      on: {
        NOT_FOUND: { target: 'NOT_FOUND' },
      },
    },
    NOT_FOUND: {},
  },
});
